import React from 'react';
import { Route } from 'react-router-dom';

import { lazy } from '@components/common/routeLoading';
import { Path } from '@components/routes/constants';
import { RedirectRoute } from '@components/routes/redirect';
import { entitled } from '@components/routes/routeEntitlement';
import { useHasAudienceCohorts } from '@components/routes/entitlements';
import { ACTION, SECTION } from '@hooks/useDocumentTitle/constants';

const COHORT_NOT_FOUND_MESSAGE =
  'Audience Cohort project not found or insufficient privileges. Redirecting to  homepage.';

/**
 * Every element is lazy. The route definitions themselves are imported eagerly
 * by the router, so anything referenced directly here would land in the initial
 * bundle — previously the whole section was behind a single lazy() on the
 * component that owned the <Routes> block.
 */
const AudienceCohortLayout = lazy(() =>
  import(/* webpackChunkName: "AudienceCohortRoutes" */ './routesLayout').then((module) => ({
    default: module.AudienceCohortLayout,
  })),
);

const AudienceCohortList = lazy(() =>
  import(/* webpackChunkName: "AudienceCohortRoutes" */ './audiencesList').then((module) => ({
    default: module.AudienceCohortList,
  })),
);

const AudienceCohortSetup = lazy(() =>
  import(/* webpackChunkName: "AudienceCohortRoutes" */ './createFlow').then((module) => ({
    default: module.AudienceCohortSetup,
  })),
);

const AudienceCohortDetails = lazy(() =>
  import(/* webpackChunkName: "AudienceCohortRoutes" */ './audienceDetails').then((module) => ({
    default: module.AudienceCohortDetails,
  })),
);

/** See the note in @components/botManagement/routes on why these are definitions. */
export const audienceCohortRoutes = (
  <Route
    key={Path.COHORT_AUDIENCES}
    path={Path.COHORT_AUDIENCES}
    element={entitled(useHasAudienceCohorts, <AudienceCohortLayout />)}
    handle={{ section: SECTION.cohortAudiences }}
  >
    <Route index element={<AudienceCohortList />} />
    <Route path="setup" element={<AudienceCohortSetup />} handle={{ action: ACTION.create }} />
    <Route path=":id" element={<AudienceCohortDetails />} />
    <Route
      path="*"
      element={<RedirectRoute to={Path.COHORT_AUDIENCES} message={COHORT_NOT_FOUND_MESSAGE} />}
    />
  </Route>
);
